// src/auditoria/components/ds/DsEmptyState.tsx
//
// Implementa o bloco de estado vazio inline do canvas — 10 artboards
// (DESIGN-SYSTEM.md §4.5).
//
// Círculo 56×56 em `background.neutral`, `border-radius:50%`, com `DsIcon` de
// traço dentro; título 15px/700; descrição 12–13px em `text.secondary`; bloco
// centralizado com `padding:48px 24px` **dentro do card da grade, mantendo o
// `.thead` visível acima** — é por isso que ele é um filho do casulo, e não um
// substituto da grade.
//
// `EmptyContent` (`src/components/empty-content/empty-content.tsx:52-67`) sempre
// renderiza uma `<img>` de até 160px de `ic-content.svg` e substitui a grade
// inteira — é a diferença mais gritante entre estado vazio real e canvas.
// Não usar dentro de grade.

import type { ReactNode } from 'react';

import Box from '@mui/material/Box';

import { DsIcon } from './DsIcon';

import type { DsIconName } from './DsIcon';

// ----------------------------------------------------------------------

export type DsEmptyStateProps = {
  icon: DsIconName;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
};

export function DsEmptyState({ icon, title, description, action }: DsEmptyStateProps) {
  return (
    <Box sx={{ padding: '48px 24px', textAlign: 'center' }}>
      <Box
        sx={{
          width: 56,
          height: 56,
          display: 'grid',
          placeItems: 'center',
          margin: '0 auto 14px',
          borderRadius: '50%',
          backgroundColor: 'background.neutral',
          color: 'text.disabled',
        }}
      >
        <DsIcon name={icon} size={22} />
      </Box>

      <Box component="h3" sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.5, m: 0 }}>
        {title}
      </Box>

      {description && (
        <Box
          component="p"
          sx={{
            fontSize: 13,
            lineHeight: 1.5,
            color: 'text.secondary',
            maxWidth: 360,
            m: '6px auto 0',
          }}
        >
          {description}
        </Box>
      )}

      {action && <Box sx={{ mt: '16px' }}>{action}</Box>}
    </Box>
  );
}
